import PageHero from "@/components/PageHero";
import { Container, FadeIn, SectionLabel, SectionHeading, SectionDivider } from "@/components/Section";
import SEOHead from "@/components/SEOHead";
import NextStepsBlock from "@/components/NextStepsBlock";
import AcuityScheduleBlock from "@/components/AcuityScheduleBlock";
import PartnersStrip from "@/components/PartnersStrip";
import SocialEcosystem from "@/components/SocialEcosystem";
import { IMG } from "@/lib/data";
import { Lightbulb, Workflow, Wrench, Camera, MicVocal, Cpu } from "lucide-react";

export default function KultureLab() {
  return (
    <div data-testid="kulture-lab-page" className="bg-[#050505]">
      <SEOHead
        title="Kulture Lab · Studio Residency for Immersive & AI Creators · Temple of Kulture"
        description="Studio time, mentorship, projection rig and AI tooling for global majority artists. Residencies, drop-in days and project incubation at Hypha Studios, Medway."
        image={IMG.xr || IMG.heroPortal}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Studio residency & creative incubator",
          provider: { "@type": "Organization", name: "Temple of Kulture" },
          areaServed: "United Kingdom",
        }}
      />
      <PageHero
        eyebrow="For creators"
        title="A studio, a rig, a community."
        kicker="Kulture Lab"
        copy="Half-day studios. Mentor-led residencies. The projection rig, the XR kit, the AI tooling — and a peer group that already knows what you're trying to build."
        bg={IMG.xr || IMG.heroPortal}
        focalMobile="50% 30%"
        focalDesktop="center"
        primaryCta={{ to: "#book-studio", label: "Book studio time" }}
        secondaryCta={{ to: "/join", label: "Apply for residency" }}
      />

      <section className="py-24 sm:py-28 bg-zinc-950 border-y border-amber-500/10">
        <Container>
          <div className="grid lg:grid-cols-12 gap-12">
            <FadeIn className="lg:col-span-6">
              <SectionLabel>Who Kulture Lab is for</SectionLabel>
              <SectionHeading className="mt-5">
                The artists building <br />
                <span className="text-amber-400">what's next.</span>
              </SectionHeading>
              <p className="mt-6 text-zinc-300 text-lg leading-relaxed">
                Independent artists, designers, technologists and producers
                making immersive work — and rarely able to afford the kit or
                the time to do it properly. Kulture Lab opens those doors:
                projection mapping rig, motion capture, volumetric video,
                XR headsets, AI image and audio tools, plus mentors who've
                shipped commercial immersive work.
              </p>
              <p className="mt-4 text-zinc-300 leading-relaxed">
                We prioritise global majority creators, women, non-binary
                artists and creators living in Medway, Kent and the wider
                south-east — but if you have a project that resonates, talk
                to us regardless.
              </p>
            </FadeIn>
            <FadeIn delay={0.1} className="lg:col-span-6 grid grid-cols-2 gap-3">
              <Tile Icon={Workflow} t="Projection rig" d="Full-scale dual-projector mapping setup." />
              <Tile Icon={Camera} t="Volumetric capture" d="Capture performers in 3D." />
              <Tile Icon={MicVocal} t="Immersive audio" d="Spatial sound design workstation." />
              <Tile Icon={Cpu} t="AI tooling" d="Image / video / text generation, mentored use." />
              <Tile Icon={Lightbulb} t="Mentorship" d="Studio days with practising immersive artists." />
              <Tile Icon={Wrench} t="Production support" d="From prototype to commercial pitch." />
            </FadeIn>
          </div>
        </Container>
      </section>

      <SectionDivider label="Book studio time" />
      <Container className="pb-20 max-w-5xl" id="book-studio">
        <div className="grid md:grid-cols-2 gap-4">
          <AcuityScheduleBlock
            eyebrow="Drop in"
            title="Half-day studio residency"
            copy="Studio access, projection rig and AI tools for one focused half-day. Mentor present."
            slug="kulture-lab"
            audience="Creators"
            duration="Half-day"
            fallbackTo="/join"
            fallbackLabel="Register interest"
            testId="acuity-lab-halfday"
          />
          <AcuityScheduleBlock
            eyebrow="Project incubation"
            title="6-week incubation track"
            copy="Concept → prototype → showcase. Studio, mentorship, peer cohort. Funded places available."
            slug="kulture-lab"
            audience="Selected creators"
            duration="6 weeks"
            fallbackTo="/join"
            fallbackLabel="Apply"
            testId="acuity-lab-incubation"
          />
        </div>
      </Container>

      <SocialEcosystem
        eyebrow="From the lab"
        title="What's being built right now"
        copy="Residency work-in-progress, open studio nights, prototypes — across every platform."
      />

      <section className="py-20 sm:py-24 bg-zinc-950 border-t border-amber-500/10">
        <PartnersStrip
          eyebrow="Lab partners"
          title="Real studios, real funders, real creative networks"
          programmeKey="experiences"
          variant="card"
          testId="lab-partners"
        />
      </section>

      <NextStepsBlock
        title="Continue the journey"
        eyebrow="What's next"
        steps={[
          { label: "Bigger commission", title: "XR Studio — production team", desc: "Take your concept into a commercial production.", to: "/xr-studio", cta: "See work", testid: "lab-next-xr" },
          { label: "Develop", title: "LEVEL UP development pathway", desc: "For 16–25 creators not yet established.", to: "/level-up-xr", cta: "Apply", testid: "lab-next-level-up" },
          { label: "Show your work", title: "TOK XR Festival 2026", desc: "Premier your immersive work on our autumn stage.", to: "/festivals", cta: "Festival", testid: "lab-next-festival" },
        ]}
      />
    </div>
  );
}

function Tile({ Icon, t, d }) {
  return (
    <div className="border border-white/5 bg-black p-5">
      <Icon className="h-5 w-5 text-amber-400" />
      <p className="mt-3 font-display font-bold text-sm">{t}</p>
      <p className="mt-1 text-xs text-zinc-400 leading-relaxed">{d}</p>
    </div>
  );
}
